'use client';

import { Textarea } from '#/components/ui/textarea';
import PasteButton from './paste-button';
import { ANON_TEXT_LIMIT_MESSAGE, spellerLength } from '#/data/constants';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';

export default function TextsArea({
  text,
  setText,
  isUser = true,
  autoFocus = false,
  headerSlot
}: {
  text: string;
  setText: Dispatch<SetStateAction<string>>;
  isUser?: boolean;
  /**
   * 진입 즉시 입력창에 포커스를 준다(#281). /correct·/analyze·/tokenize 모두
   * 켠다. 모바일(coarse pointer)은 가상 키보드가 화면을 덮으므로 제외.
   */
  autoFocus?: boolean;
  /**
   * 헤더 행("원문  0 / 1,000") 우측 여백에 붙일 내용.
   *
   * 이 컴포넌트는 /correct·/analyze·/tokenize 가 공유하므로, 특정 페이지에서만
   * 필요한 안내는 여기서 조건 분기하지 말고 호출부에서 주입한다. 행 높이는
   * 왼쪽 h3(text-xl)가 정하니 그보다 작은 글씨를 넘겨야 검사 영역이 밀리지 않는다.
   */
  headerSlot?: ReactNode;
}) {
  const [error, setError] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!autoFocus) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    textareaRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 비로그인만 글자 수 제한, 로그인 유저는 무제한
  const limit = !isUser ? spellerLength : null;

  useEffect(() => {
    if (limit !== null && text.length > limit) {
      setError(true);
    } else {
      setError(false);
    }
  }, [text, limit]);

  return (
    /*
      좌우 여백은 여기서 한 번에 준다. 라벨·글자 수·입력창이 같은 컨테이너
      안에서 함께 밀려야 첫 글자와 '원문' 라벨의 왼쪽이 계속 맞는다. 종전에는
      textarea 에만 여백을 줘서 왼쪽이 카드 여백(px-6)에 그대로 붙어 있었다.
    */
    <div className="flex flex-col gap-4 w-full flex-1 h-full overflow-hidden px-4">
      <div className="flex gap-4 items-center">
        <h3 className="text-xl font-bold">원문 </h3>
        <p className="text-sm">
          {text.length.toLocaleString()}
          {limit !== null && ` / ${limit.toLocaleString()}`}
        </p>
        {/*
          붙여넣기는 입력창 바로 위, 글자 수 옆에 둔다. 아래 버튼 줄에 있을 때보다
          눈에 띄고 입력 시작 동선과도 붙는다. 이미 쓴 글을 덮어쓰지 않도록
          비어 있을 때만 노출한다.
        */}
        {text.trim().length === 0 && (
          <PasteButton
            onPaste={(clip) => {
              setText(clip);
              textareaRef.current?.focus({ preventScroll: true });
            }}
          />
        )}
        {/* 글자 수 초과 안내가 유도 문구보다 우선한다 — 한 줄에 둘 다 두지 않는다. */}
        {error && limit !== null ? (
          <p className="min-w-0 text-sm text-red-500 dark:text-red-400">
            {ANON_TEXT_LIMIT_MESSAGE}
          </p>
        ) : (
          headerSlot && <div className="ml-auto min-w-0">{headerSlot}</div>
        )}
      </div>
      {/*
        입력 영역에 테두리와 배경을 준다. 종전에는 textarea 가 border-0 에 배경도
        투명해서 입력 필드로 보이는 경계가 없었다. 라이트에서는 글이 카드 배경
        위에 그냥 얹힌 듯 보이고, 다크에서는 여백에서 글이 시작되는 듯 보였다.
        경계가 생기면 안쪽 padding 도 눈에 드러난다.
      */}
      <div
        className="h-full overflow-auto rounded-lg border border-[#104573]/15 bg-white transition-colors
          focus-within:border-[#104573]/40 dark:border-white/10 dark:bg-white/[0.04]
          dark:focus-within:border-[#60a3d9]/50"
      >
        <Textarea
          ref={textareaRef}
          placeholder="내용을 입력해주세요"
          value={text}
          onChange={(e) => setText(e.target.value)}
          /* 여백은 입력 박스 안쪽에서 준다. 테두리가 있으니 눈에 드러난다. */
          className="h-full resize-none border-0 bg-transparent px-4 py-3 shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  );
}
